import React from "react";
// ANT DESIGN COMPONENTS
import { Tag, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CONFIG
import { ActionTypeConfig } from "../actionTypeConfig";

/**
 * Relative luminance of a `#rrggbb` colour, per WCAG 2.x.
 *
 * The tag is filled with the type's accent, so the text on top has to be picked
 * from the fill rather than from the theme: the same white that reads cleanly on
 * the purple SWITCH accent is unreadable on the yellow HOLDBACK one.
 */
const luminance = (hex: string): number => {
  const value = hex.replace("#", "");
  if (value.length !== 6) return 0;
  const channel = (offset: number) => {
    const srgb = parseInt(value.slice(offset, offset + 2), 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
};

/** Near-black on light fills, white on dark ones — whichever contrasts more. */
const readableTextOn = (hex: string): string => (luminance(hex) > 0.45 ? "#1f1f1f" : "#ffffff");

/**
 * Fixed-width chip naming the action's button type, filled with that type's
 * accent — the same colour as the card's left edge, so the edge and the chip
 * name the same thing instead of being two independent cues.
 *
 * Renders the config's readable label ("Toggle Timer"), never the raw enum name
 * the API sends ("TOGGLETIMER", "ENERGIZER_TURN_BACK"). The width is fixed so
 * the chips form a column down the list rather than a ragged edge; a label too
 * long for it truncates and the full text stays available on hover, since the
 * type is also carried by the icon and the accent colour.
 */
export default function ActionTypeTag({
  config,
  theme,
}: {
  config: ActionTypeConfig;
  theme: string;
}) {
  const [t] = useTranslation("global");
  const label = t(config.labelKey, config.label);

  return (
    <Tooltip
      title={label}
      placement="topLeft"
      overlayClassName={`monitoring__notification__${theme}`}
    >
      {/* The `actionCard__tag__{theme}` variant is deliberately absent: it sets
          colours with !important, which would win over the accent fill below.
          Contrast is handled by readableTextOn instead, which does not depend
          on the theme because the fill does not either. */}
      <Tag
        className="actionCard__tag actionCard__tag--typed"
        style={{ backgroundColor: config.accent, borderColor: config.accent }}
      >
        <span className="actionCard__tagLabel" style={{ color: readableTextOn(config.accent) }}>
          {label}
        </span>
      </Tag>
    </Tooltip>
  );
}
